import { useEffect, useState } from 'react'
import { useRecorder } from '../state/recorder'
import { useTours } from '../state/tours'
import { useUi } from '../state/ui'
import { formatKm, formatSpeed, simplify } from '../lib/geo'
import type { LngLatEle } from '../lib/geo'
import { SPORTS } from '../lib/models'
import { IconPause, IconPlay, IconStop } from './icons'

function fmtClock(totalS: number): string {
  const h = Math.floor(totalS / 3600)
  const m = Math.floor((totalS % 3600) / 60)
  const s = Math.floor(totalS % 60)
  return `${h ? h + ':' : ''}${String(m).padStart(h ? 2 : 1, '0')}:${String(s).padStart(2, '0')}`
}

export default function RecordBar() {
  const r = useRecorder()
  const showToast = useUi((s) => s.showToast)
  const [, tick] = useState(0)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')

  useEffect(() => {
    if (r.status !== 'recording') return
    const t = setInterval(() => tick((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [r.status])

  if (r.status === 'idle') return null

  const elapsedS = r.startedAt ? (Date.now() - r.startedAt) / 1000 : 0
  const speed = r.lastFix?.coords.speed ?? null
  const sportLabel = SPORTS.find((s) => s.id === r.sport)?.label ?? 'Activity'

  const finish = async () => {
    const sport = useRecorder.getState().sport
    const result = useRecorder.getState().stop()
    setSaving(false)
    if (!result) {
      showToast('Recording discarded — not enough GPS points')
      return
    }
    const coords: LngLatEle[] = result.points
    const tourName = name.trim() || `${sportLabel} · ${new Date(result.startedAt).toLocaleDateString()}`
    await useTours.getState().save({
      id: crypto.randomUUID(),
      name: tourName,
      sport,
      kind: 'recorded',
      data: {
        coords: coords.length > 6000 ? simplify(coords, 2) : coords,
        times: coords.length > 6000 ? undefined : result.times,
        gaps: result.gaps.length ? result.gaps : undefined,
        stats: {
          distanceM: Math.round(result.distanceM),
          ascentM: Math.round(result.ascentM),
          descentM: 0,
          durationS: Math.round((Date.now() - result.startedAt) / 1000),
          movingTimeS: Math.round(result.movingTimeS),
        },
      },
      createdAt: result.startedAt,
      updatedAt: Date.now(),
    })
    setName('')
    showToast(`Saved “${tourName}”`)
    useUi.getState().setPanel('tours')
  }

  return (
    <div className="record-bar">
      {r.gpsError && <p style={{ fontSize: 12.5, color: '#ffa8a8', margin: '0 0 10px', fontWeight: 600 }}>GPS: {r.gpsError}</p>}
      <div className="stats">
        <div className="stat">
          <div className="v">{fmtClock(elapsedS)}</div>
          <div className="l">
            {r.status === 'paused' ? (
              'Paused'
            ) : (
              <>
                <span className="rec-pulse" />
                {sportLabel}
              </>
            )}
          </div>
        </div>
        <div className="stat">
          <div className="v">{formatKm(r.distanceM)}</div>
          <div className="l">Distance</div>
        </div>
        <div className="stat">
          <div className="v">{speed != null && speed >= 0 ? formatSpeed(speed) : '—'}</div>
          <div className="l">Speed</div>
        </div>
        <div className="stat">
          <div className="v">{Math.round(r.ascentM)} m</div>
          <div className="l">Ascent</div>
        </div>
      </div>

      {saving ? (
        <>
          <input
            className="field"
            style={{ marginBottom: 10 }}
            placeholder="Name this activity"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void finish()}
            autoFocus
          />
          <div className="record-actions">
            <button className="btn stop" onClick={() => void finish()}>
              Save activity
            </button>
            <button className="btn" onClick={() => setSaving(false)}>
              Back
            </button>
            <button
              className="btn"
              onClick={() => {
                useRecorder.getState().discard()
                showToast('Recording discarded')
              }}
            >
              Discard
            </button>
          </div>
        </>
      ) : (
        <div className="record-actions">
          {r.status === 'recording' ? (
            <button className="btn" onClick={r.pause}>
              <IconPause /> Pause
            </button>
          ) : (
            <button className="btn" onClick={r.resume}>
              <IconPlay /> Resume
            </button>
          )}
          <button
            className="btn stop"
            onClick={() => {
              if (r.status === 'recording') r.pause()
              setSaving(true)
            }}
          >
            <IconStop /> Finish
          </button>
        </div>
      )}
    </div>
  )
}
